import React, { ReactChild, ReactFragment, ReactPortal, useEffect } from 'react';
import ListSubheader from '@mui/material/ListSubheader';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import { OptionTypeWithCode } from '../types';
import { useResetCache } from '../../VirtualList/impl/useResetCache';
import { styled } from '../../../styles';
import { OuterElementContext, OuterElementType } from '../../VirtualList/impl/OuterElementContext';

const GROUP_LABEL_HEIGHT = 48;
const OPTION_LABEL_HEIGHT = 41;
const OPTION_CODE_HEIGHT = 13;
const LISTBOX_PADDING = 8;
const SECONDARY_SUB_LABEL_HEIGHT = 24;

export interface ListboxComponentProps {
  /*! width of the dropdown menu */
  width?: string;
  children?: React.ReactNode;
  scrollSelectedItemIntoView?: boolean;
  optionHeight?: number;
}

// Adapter for react-window
export const ListboxComponent = React.forwardRef<HTMLDivElement, ListboxComponentProps>((props, ref) => {
  const { children, width, scrollSelectedItemIntoView, optionHeight, ...other } = props;

  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;

  const gridRef = useResetCache(itemCount);

  useEffect(() => {
    // Scroll to selected item.
    if (scrollSelectedItemIntoView && props?.children && gridRef.current) {
      const childrenArray = React.Children.toArray(props.children);
      const index = childrenArray.findIndex((child) => React.isValidElement(child) && child.props['aria-selected']);
      if (index === -1) return;

      setTimeout(() => {
        const height = getHeight(itemCount, itemData, optionHeight);
        const padding = 2 * LISTBOX_PADDING;
        const listBoxMidPoint = (height - padding) / 2;
        const childSize = getChildSize(childrenArray[index]);
        const scrollOffset = childSize * (index + 1) - listBoxMidPoint;
        gridRef.current?.scrollTo(scrollOffset);
      }, 0); // setTimeout is needed to wait for the grid to render
    }
  }, [gridRef, props?.children, scrollSelectedItemIntoView, itemCount, itemData, optionHeight]);

  return (
    <div ref={ref} className="virtual-listbox-component">
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight(itemCount, itemData, optionHeight) + 2 * LISTBOX_PADDING}
          width={width ?? '100%'}
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType={StyledListItemWrapper}
          itemSize={(index) => getChildSize(itemData[index], optionHeight)}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const getChildSize = (child?: ReactChild | ReactFragment | ReactPortal, optionHeight?: number) => {
  if (React.isValidElement(child) && child.type === ListSubheader) {
    return GROUP_LABEL_HEIGHT;
  }
  if (typeof optionHeight === 'number') {
    return optionHeight;
  }
  const item: OptionTypeWithCode = (child as ReactPortal)?.props?.option ?? {};
  return (
    OPTION_LABEL_HEIGHT +
    (item?.code || !!item?.chip || item?.subLabel ? OPTION_CODE_HEIGHT : 0) +
    (item?.secondarySubLabel ? SECONDARY_SUB_LABEL_HEIGHT : 0)
  );
};

const NUM_ROWS = 8;
const getHeight = (
  itemCount: number,
  itemData: Array<ReactChild | ReactFragment | ReactPortal>,
  optionHeight?: number
) => {
  if (itemCount > NUM_ROWS) {
    return itemData
      .slice(0, NUM_ROWS)
      .map((child) => getChildSize(child, optionHeight))
      .reduce((a, b) => a + b, 0);
  }
  return itemData.map((child) => getChildSize(child, optionHeight)).reduce((a, b) => a + b, 0);
};

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING
  };

  return (
    <div style={inlineStyle} className="virtual-item">
      {dataSet}
    </div>
  );
}

const StyledListItemWrapper = styled('ul')`
    padding: 0;
    margin: 0;
`;
